import React, { type ReactNode } from "react";
import { Balancer } from "react-wrap-balancer";
import { Text, Box, Flex, type FlexProps, AspectRatio, type AspectRatioProps } from "@chakra-ui/react";
import Image, { type StaticImageData } from "next/image";
import { ArrowLink, type ArrowLinkProps } from "ui/link";
import { useHeaderHeight } from "contexts/header-height";

type TextImageProps = {
  align: "left" | "right";
  image: StaticImageData;
  /** Defaults to `5 / 4`. */
  imageRatio?: AspectRatioProps["ratio"];
  eyebrow?: string;
  title: string;
  description: string | ReactNode;
  actionUrl?: string;
  actionText?: string;
  linkVariant?: ArrowLinkProps["variant"];
  backgroundGradient?: ReactNode;
};

export function PairImage({
  align,
  image,
  imageRatio = 5 / 4,
  eyebrow,
  title,
  description,
  actionUrl,
  actionText = "Read more",
  linkVariant = "grass",
  backgroundGradient,
  ...props
}: TextImageProps & FlexProps) {
  const { headerHeight } = useHeaderHeight();

  return (
    <Flex
      align="center"
      direction={{ base: "column", md: "row" }}
      gap={{ base: 9, md: 16, lg: 24, xl: 40 }}
      maxW={{ base: "mediaMaxW.mobile", md: "100%" }}
      mx="auto"
      w="100%"
      scrollMarginTop={`${headerHeight + 32}px`}
      {...props}
    >
      <Box flex={1} order={{ base: 1, md: align === "left" ? 1 : 2 }} width="100%">
        {eyebrow && (
          <Text fontSize={{ base: "xs", sm: "sm", lg: "md" }} textStyle="uppercase" mb={5}>
            {eyebrow}
          </Text>
        )}

        <Text as="h3" textStyle="h6" maxW="22ch">
          <Balancer>{title}</Balancer>
        </Text>

        {typeof description === "string" ? (
          <Text fontSize={{ base: "md", md: "lg" }} mt={5} w="100%">
            {description}
          </Text>
        ) : (
          <Box mt={5}>{description}</Box>
        )}

        {actionUrl && (
          <ArrowLink href={actionUrl} variant={linkVariant} size={{ base: "md", md: "lg" }} mt={6}>
            {actionText}
          </ArrowLink>
        )}
      </Box>

      <AspectRatio
        ratio={imageRatio}
        flex={1}
        order={{ base: 2, md: align === "left" ? 2 : 1 }}
        width="100%"
        pos="relative"
        sx={{ "> div": { overflow: "visible" } }}
      >
        <Box>
          {backgroundGradient}
          <Image fill src={image} alt={`${title}.`} sizes="100%" style={{ objectFit: "contain" }} />
        </Box>
      </AspectRatio>
    </Flex>
  );
}
